import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { CalendarioFeriados, TipoFeriado } from '../models/ciclo';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { formatarData } from '../utils/formatters';
import { LABEL_TIPO_FERIADO } from '../utils/labels';

interface ModalGerenciarCalendarioProps {
  aberto: boolean;
  onFechar: () => void;
}

const TIPOS: TipoFeriado[] = ['NACIONAL', 'ESTADUAL', 'MUNICIPAL', 'EMPRESA'];

// MAESTRO-RN-23 — o backend já tinha o ciclo de vida completo (calendário versionado DRAFT →
// PUBLICADO, feriados só editáveis em DRAFT), mas nenhuma tela expunha isso — a etapa "Calendário
// de dias úteis" do wizard de abertura só mostrava uma contagem, sem forma de cadastrar nada.
const ModalGerenciarCalendario: React.FC<ModalGerenciarCalendarioProps> = ({ aberto, onFechar }) => {
  const { empresasEscopo, recarregar } = useMaestroContext();

  const [calendarios, setCalendarios] = useState<CalendarioFeriados[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [calendarioSelecionadoId, setCalendarioSelecionadoId] = useState<number | null>(null);

  const [criandoNovo, setCriandoNovo] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [tipoNovo, setTipoNovo] = useState<TipoFeriado>('NACIONAL');
  const [ufNovo, setUfNovo] = useState('');
  const [municipioNovo, setMunicipioNovo] = useState('');
  const [empresaIdNovo, setEmpresaIdNovo] = useState(0);
  const [enviandoNovo, setEnviandoNovo] = useState(false);

  const [dataFeriado, setDataFeriado] = useState('');
  const [nomeFeriado, setNomeFeriado] = useState('');
  const [enviandoFeriado, setEnviandoFeriado] = useState(false);
  const [criandoNovaVersao, setCriandoNovaVersao] = useState(false);

  const [anoImportar, setAnoImportar] = useState(new Date().getFullYear());
  const [importando, setImportando] = useState(false);

  const carregar = () => {
    setCarregando(true);
    maestroService.listarCalendarios()
      .then(lista => { setCalendarios(lista); if (lista.length > 0 && calendarioSelecionadoId === null) setCalendarioSelecionadoId(lista[0].id); })
      .catch((e: any) => setErro(e?.response?.data?.message ?? e?.message ?? 'Não foi possível carregar os calendários.'))
      .finally(() => setCarregando(false));
  };

  useEffect(() => {
    if (!aberto) return;
    setErro(null);
    setCriandoNovo(false);
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const calendarioSelecionado = calendarios.find(c => c.id === calendarioSelecionadoId) ?? null;

  const criarCalendario = () => {
    if (!nomeNovo.trim()) return;
    setEnviandoNovo(true);
    maestroService.criarCalendario({
      nome: nomeNovo.trim(),
      tipo: tipoNovo,
      uf: tipoNovo === 'ESTADUAL' ? ufNovo.trim() || undefined : undefined,
      municipio: tipoNovo === 'MUNICIPAL' ? municipioNovo.trim() || undefined : undefined,
      empresaId: tipoNovo === 'EMPRESA' ? empresaIdNovo || undefined : undefined,
    })
      .then(criado => {
        setCalendarios(prev => [...prev, criado]);
        setCalendarioSelecionadoId(criado.id);
        setCriandoNovo(false);
        setNomeNovo(''); setUfNovo(''); setMunicipioNovo(''); setEmpresaIdNovo(0);
      })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o calendário.'))
      .finally(() => setEnviandoNovo(false));
  };

  const adicionarFeriado = () => {
    if (!calendarioSelecionado || !dataFeriado || !nomeFeriado.trim()) return;
    setEnviandoFeriado(true);
    maestroService.adicionarFeriado(calendarioSelecionado.id, { data: dataFeriado, nome: nomeFeriado.trim() })
      .then(atualizado => {
        setCalendarios(prev => prev.map(c => c.id === atualizado.id ? atualizado : c));
        setDataFeriado(''); setNomeFeriado('');
      })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível adicionar o feriado.'))
      .finally(() => setEnviandoFeriado(false));
  };

  const removerFeriado = (holidayId: number) => {
    maestroService.removerFeriado(holidayId)
      .then(atualizado => setCalendarios(prev => prev.map(c => c.id === atualizado.id ? atualizado : c)))
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível remover o feriado.'));
  };

  const importarDoAno = () => {
    if (!calendarioSelecionado || !anoImportar) return;
    setImportando(true);
    maestroService.sincronizarFeriadosBrasilApi(calendarioSelecionado.id, anoImportar)
      .then(atualizado => setCalendarios(prev => prev.map(c => c.id === atualizado.id ? atualizado : c)))
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível importar os feriados. Verifique a conexão com a BrasilAPI.'))
      .finally(() => setImportando(false));
  };

  const publicar = () => {
    if (!calendarioSelecionado) return;
    if (!window.confirm('Publicar este calendário? Depois de publicado, os feriados ficam travados — só é possível editar criando uma nova versão.')) return;
    maestroService.publicarCalendario(calendarioSelecionado.id)
      .then(atualizado => { setCalendarios(prev => prev.map(c => c.id === atualizado.id ? atualizado : c)); recarregar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível publicar o calendário.'));
  };

  const criarNovaVersao = () => {
    if (!calendarioSelecionado) return;
    setCriandoNovaVersao(true);
    maestroService.criarNovaVersaoCalendario(calendarioSelecionado.id)
      .then(novaVersao => { setCalendarios(prev => [...prev, novaVersao]); setCalendarioSelecionadoId(novaVersao.id); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar uma nova versão do calendário.'))
      .finally(() => setCriandoNovaVersao(false));
  };

  const fecharEAtualizar = () => { recarregar(); onFechar(); };

  return (
    <Dialog open={aberto} onClose={fecharEAtualizar} maxWidth='md' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Calendário de Feriados</DialogTitle>
      <DialogContent>
        {erro && <p className='mc-texto-danger'>{erro}</p>}

        {carregando ? (
          <p className='mc-texto-neutro'>Carregando...</p>
        ) : (
          <>
            <div className='mc-form-inline' style={{ marginBottom: 12 }}>
              <label>Calendário
                <select value={calendarioSelecionadoId ?? ''} onChange={e => setCalendarioSelecionadoId(Number(e.target.value))} disabled={calendarios.length === 0}>
                  {calendarios.length === 0 && <option value=''>Nenhum calendário cadastrado</option>}
                  {calendarios.map(c => <option key={c.id} value={c.id}>{c.nome} — {LABEL_TIPO_FERIADO[c.tipo]} (v{c.versao}, {c.status === 'PUBLICADO' ? 'publicado' : 'rascunho'})</option>)}
                </select>
              </label>
              <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setCriandoNovo(v => !v)}>
                <i className='bi bi-plus-lg' /> Novo calendário
              </button>
            </div>

            {criandoNovo && (
              <div className='mc-form-inline' style={{ marginBottom: 16 }}>
                <label>Nome
                  <input type='text' placeholder='Ex.: Feriados Nacionais 2026' value={nomeNovo} onChange={e => setNomeNovo(e.target.value)} />
                </label>
                <label>Tipo
                  <select value={tipoNovo} onChange={e => setTipoNovo(e.target.value as TipoFeriado)}>
                    {TIPOS.map(t => <option key={t} value={t}>{LABEL_TIPO_FERIADO[t]}</option>)}
                  </select>
                </label>
                {tipoNovo === 'ESTADUAL' && (
                  <label>UF<input type='text' maxLength={2} placeholder='Ex.: SP' value={ufNovo} onChange={e => setUfNovo(e.target.value.toUpperCase())} /></label>
                )}
                {tipoNovo === 'MUNICIPAL' && (
                  <label>Município<input type='text' placeholder='Ex.: São Paulo' value={municipioNovo} onChange={e => setMunicipioNovo(e.target.value)} /></label>
                )}
                {tipoNovo === 'EMPRESA' && (
                  <label>Empresa
                    <select value={empresaIdNovo} onChange={e => setEmpresaIdNovo(Number(e.target.value))}>
                      <option value={0}>Selecione...</option>
                      {empresasEscopo.map(emp => <option key={emp.id} value={emp.id}>{emp.nome}</option>)}
                    </select>
                  </label>
                )}
                <button type='button' className='mc-btn mc-btn--primary' disabled={enviandoNovo || !nomeNovo.trim()} onClick={criarCalendario}>
                  {enviandoNovo ? 'Criando...' : 'Criar calendário'}
                </button>
              </div>
            )}

            {!calendarioSelecionado ? (
              <p className='mc-texto-neutro'>Crie um calendário para começar a cadastrar feriados.</p>
            ) : (
              <>
                {calendarioSelecionado.status === 'PUBLICADO' ? (
                  <div className='mc-form-inline' style={{ alignItems: 'center' }}>
                    <p className='mc-texto-neutro' style={{ margin: 0 }}><i className='bi bi-lock-fill' /> Calendário publicado (v{calendarioSelecionado.versao}) — feriados travados.</p>
                    <button type='button' className='mc-btn mc-btn--secondary' disabled={criandoNovaVersao} onClick={criarNovaVersao}>
                      {criandoNovaVersao ? 'Criando...' : 'Criar nova versão para editar'}
                    </button>
                  </div>
                ) : (
                  <>
                    {calendarioSelecionado.tipo === 'NACIONAL' && (
                      <div className='mc-form-inline' style={{ marginBottom: 12 }}>
                        <label>Importar feriados nacionais do ano
                          <input type='number' min={2000} max={2100} value={anoImportar} onChange={e => setAnoImportar(Number(e.target.value))} />
                        </label>
                        <button type='button' className='mc-btn mc-btn--primary' disabled={importando} onClick={importarDoAno}>
                          <i className='bi bi-cloud-download' /> {importando ? 'Importando...' : 'Importar da BrasilAPI'}
                        </button>
                      </div>
                    )}
                    <div className='mc-form-inline' style={{ marginBottom: 14 }}>
                      <label>Data<input type='date' value={dataFeriado} onChange={e => setDataFeriado(e.target.value)} /></label>
                      <label>Nome do feriado<input type='text' placeholder='Ex.: Confraternização Universal' value={nomeFeriado} onChange={e => setNomeFeriado(e.target.value)} /></label>
                      <button type='button' className='mc-btn mc-btn--secondary' disabled={enviandoFeriado || !dataFeriado || !nomeFeriado.trim()} onClick={adicionarFeriado}>
                        {enviandoFeriado ? 'Adicionando...' : 'Adicionar feriado avulso'}
                      </button>
                    </div>
                  </>
                )}

                {calendarioSelecionado.feriados.length === 0 ? (
                  <p className='mc-texto-neutro'>Nenhum feriado cadastrado neste calendário ainda.</p>
                ) : (
                  <div className='mc-membros-lista'>
                    {calendarioSelecionado.feriados.map(f => (
                      <div key={f.id} className='mc-membro-card'>
                        <div className='mc-membro-card__nome'>{f.nome}</div>
                        <div className='mc-membro-card__email'>{formatarData(f.data)}</div>
                        {calendarioSelecionado.status === 'DRAFT' && (
                          <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => removerFeriado(f.id)}>Remover</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {calendarioSelecionado.status === 'DRAFT' && (
                  <button type='button' className='mc-btn mc-btn--secondary' style={{ marginTop: 14 }} onClick={publicar}>
                    <i className='bi bi-check2-circle' /> Publicar calendário
                  </button>
                )}
              </>
            )}
          </>
        )}

        <div className='mc-wizard-acoes' style={{ marginTop: 20 }}>
          <button type='button' className='mc-btn mc-btn--secondary' onClick={fecharEAtualizar}>Fechar</button>
          <div />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalGerenciarCalendario;
